import type { FileWithContent } from "schemas/generated/file";

import editableListenerContents from "../base-files/editableListener?raw";

export const getSandpackFilePath = (file: FileWithContent) => {
  if (file.fileType === "component") {
    return `/src${file.path === "/" ? "" : file.path}`;
  } else {
    if (file.path === "/") {
      return `/src${file.exportName}`;
    }
    return `/src${file.path === "/" ? "" : file.path}`;
  }
};

export const findPageFileFromPath = (
  path: string,
  files: FileWithContent[],
) => {
  return files.find((f) => f.path === path && f.fileType === "page");
};

export const convertFilesToSandpackFiles = (
  files: FileWithContent[],
  productIds: string[],
  openPages: string[],
  displayMode: "preview" | "edit",
) => {
  const fileSystem = files.reduce((acc, file) => {
    const filePath = getSandpackFilePath(file);
    return {
      ...acc,
      [filePath]: file.content,
    };
  }, {});

  return {
    ...fileSystem,
    ...createDefaultAppFile(openPages, productIds, files, displayMode),
    ...createProductsLoader(),
    ...(displayMode === "edit"
      ? { "/editableListener.ts": editableListenerContents }
      : {}),
  };
};

const createDefaultAppFile = (
  openPages: string[],
  productIds: string[],
  files: FileWithContent[],
  displayMode: "preview" | "edit",
) => {
  return {
    "/App.tsx": `import React from 'react';
  import { BrowserRouter, Routes, Route } from 'react-router-dom';
  import ProductsLoader from './src/base-components/products-loader';
  ${displayMode === "edit" ? `import './editableListener';` : ""}
  ${openPages
    .map((path) => {
      const pageFile = findPageFileFromPath(path, files);
      if (!pageFile) {
        return null;
      }
      return `import ${pageFile?.exportName} from '.${getSandpackFilePath(pageFile)}';`;
    })
    .join("\n")}

  const App: React.FC = () => {
  return (
    <ProductsLoader productIds={[${productIds.map((id) => `"${id}"`).join(", ")}]}>
      <BrowserRouter>
      <Routes>
  ${openPages
    .map((path) => {
      const pageFile = findPageFileFromPath(path, files);
      if (!pageFile) {
        return null;
      }
      return `        <Route path="${path}" element={<${pageFile?.exportName} />} />`;
    })
    .join("\n")}
      </Routes>
      </BrowserRouter>
    </ProductsLoader>
  );
  };

  export default App;`,
  };
};

// ToDo: these files should be moved to a seperate file
const createProductsLoader = () => {
  return {
    "src/lib/iframeMessaging.ts": `import { v4 as uuidV4 } from "uuid";

const QUERY_TIMEOUT_MS = 15000;

type ProductQuery = {
  type: "requestProduct";
  payload: {
    productId: string;
  };
};

// Add more query types here as needed
type ParentFrameQuery = ProductQuery;

export function messageParent(query: ParentFrameQuery) {
  return new Promise((resolve, reject) => {
    const queryId = uuidV4();

    const handleMessage = (event: MessageEvent) => {
      if (event.data.id === queryId) {
        window.removeEventListener("message", handleMessage);
        resolve(event.data);
      }
    };

    window.addEventListener("message", handleMessage);
    window.parent.postMessage(
      { type: query.type, id: queryId, payload: query.payload },
      "*",
    );

    setTimeout(() => {
      window.removeEventListener("message", handleMessage);
      reject(new Error("Timeout"));
    }, QUERY_TIMEOUT_MS);
  });
}
`,
    "src/hooks/useProducts.ts": `import type { Product } from "../lib/products";

import { useEffect, useState } from "react";

import { messageParent } from "../lib/iframeMessaging";

async function fetchProductsInIframe(productIds: string[]): Promise<Product[]> {
  if (typeof window === "undefined" || typeof window.parent === "undefined") {
    return [];
  }

  if (!productIds || productIds.length === 0) {
    return [];
  }

  const uniqueProductIds = Array.from(
    new Set(productIds.map((id) => id.toString())),
  );

  const productPromises = uniqueProductIds.map((productId) =>
    messageParent({
      type: "requestProduct",
      payload: {
        productId,
      },
    })
      .then((response: any) => {
        if (
          response &&
          response.type === "returnProduct" &&
          response.payload &&
          response.payload.product
        ) {
          return response.payload.product as Product;
        }
        return undefined;
      })
      .catch(() => undefined),
  );

  const products = await Promise.all(productPromises);

  return products.filter((p): p is Product => p !== undefined);
}

interface UseProductsReturn {
  products: Product[];
  isLoading: boolean;
}

export function useProducts(productIds: string[]): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const productIdsString = JSON.stringify((productIds || []).slice().sort());

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const idsToFetch = productIds || [];
    fetchProductsInIframe(idsToFetch)
      .then((fetchedProducts) => {
        if (isMounted) {
          setProducts(fetchedProducts);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setProducts([]);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [productIdsString]);

  return { products, isLoading };
}
`,
    "src/base-components/products-loader.tsx": `import { ProductsProvider } from "../context/ProductsProvider";
import { useProducts } from "../hooks/useProducts";

export default function ProductLoader({
  productIds,
  children,
}: {
  productIds: string[];
  children: React.ReactNode;
}) {
  const { products, isLoading } = useProducts(productIds);

  return (
    <ProductsProvider products={isLoading ? [] : products}>
      {children}
    </ProductsProvider>
  );
}
`,
  };
};
